<script>
// Pile des modales ouvertes, partagee par toutes les instances (niveau module) :
// seule la derniere ouverte (au sommet) reagit au bouton retour systeme, sinon
// deux modales empilees se fermeraient d'un seul geste.
const openStack = [];
</script>

<script setup>
import { X } from 'lucide-vue-next';
import { watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  maxWidth: {
    type: String,
    default: 'max-w-4xl'
  },
  showClose: {
    type: Boolean,
    default: true
  },
  closeOnBackdrop: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

// Identifiant de cette instance dans la pile des modales ouvertes.
const stackId = Symbol('modal');
function pushOpen() {
  if (!openStack.includes(stackId)) openStack.push(stackId);
}
function popOpen() {
  const i = openStack.indexOf(stackId);
  if (i !== -1) openStack.splice(i, 1);
}

// Bouton retour systeme (Android, relaye par App.vue) : ferme la modale ouverte
// et consomme l'evenement pour que la page ne recule pas en meme temps.
function onSystemBack(e) {
  if (!props.isOpen || e.defaultPrevented) return;
  if (openStack[openStack.length - 1] !== stackId) return;
  e.preventDefault();
  emit('close');
}
onMounted(() => window.addEventListener('clocook:back', onSystemBack));

// Lock body scroll when modal is open
watch(() => props.isOpen, (val) => {
  if (val) {
    pushOpen();
    document.body.style.overflow = 'hidden';
  } else {
    popOpen();
    document.body.style.overflow = '';
  }
}, { immediate: true });

onUnmounted(() => {
  popOpen();
  document.body.style.overflow = '';
  window.removeEventListener('clocook:back', onSystemBack);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 h-full">

      <div
        class="bg-white rounded-2xl md:rounded-[32px] w-full max-h-[90dvh] md:max-h-[85vh] overflow-hidden shadow-2xl relative flex flex-col border border-gray-100"
        :class="maxWidth"
      >
          
          <!-- Header -->
          <div v-if="title || showClose || $slots.header" class="p-4 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10 shrink-0 gap-4">
            <div class="flex-1 min-w-0">
              <slot name="header">
                 <div v-if="title">
                    <h2 class="text-xl md:text-2xl font-bold text-gray-900 font-baloo leading-tight">{{ title }}</h2>
                    <p v-if="subtitle" class="text-xs md:text-sm text-gray-500">{{ subtitle }}</p>
                 </div>
              </slot>
            </div>
            <button v-if="showClose" @click="$emit('close')" class="p-2 -mr-2 md:mr-0 rounded-full hover:bg-gray-100 transition-colors shrink-0">
              <X class="w-5 h-5 md:w-6 md:h-6 text-gray-500" />
            </button>
          </div>
          
          <!-- Content -->
          <div class="flex-1 overflow-y-auto overflow-x-hidden min-h-0 bg-gray-50/50 overscroll-contain touch-pan-y" style="-webkit-overflow-scrolling: touch;">
            <slot />
          </div>

        <!-- Footer -->
        <div v-if="$slots.footer" class="p-4 border-t border-gray-100 bg-white shrink-0">
          <slot name="footer" />
        </div>

      </div>
    </div>
  </Teleport>
</template>
